import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  Calendar,
  Users,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
} from "lucide-react";
import logo from "../../Images/SelfSync Logo.png";
import moment from "moment/moment";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show CTA section on schedule-demo page
  const showCTASection = ![
    "/schedule-demo",
    "/student-placement-cell",
  ].includes(location.pathname);

  const quickLinks = [
    { name: "How It Works", path: "/how-it-works" },
    { name: "Pricing", path: "/pricing" },
    { name: "FAQ", path: "/faq" },
    { name: "Sample Report", path: "/sample-report" },
  ];

  const company = [
    { name: "About Us", path: "/about-us" },
    { name: "Contact Us", path: "/contact-us" },
    { name: "Partner With Us", path: "/partner-with-us" },
    { name: "Blogs", path: "/blogs" },
    { name: "Schedule Demo", path: "/schedule-demo" },
    { name: "Student Placement Cell", path: "/student-placement-cell" },
  ];

  const legal = [
    { name: "Terms of Service", path: "/terms-of-service" },
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Cancellation & Refund", path: "/cancellation-refund" },
  ];

  const socialLinks = [
    {
      name: "LinkedIn",
      url: "https://www.linkedin.com/company/selfsyncai/",
      icon: <Linkedin className="w-5 h-5" />,
    },
    {
      name: "Facebook",
      url: "https://www.facebook.com/profile.php?id=61575265121460",
      icon: <Facebook className="w-5 h-5" />,
    },
    {
      name: "Instagram",
      url: "https://www.instagram.com/selfsync.ai?igsh=YzNpYTY4eW9yMHRp",
      icon: <Instagram className="w-5 h-5" />,
    },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      {/* CTA Section - Only show when NOT on schedule-demo page */}
      {showCTASection && (
        <div className="bg-gradient-to-r from-purple-600 to-cyan-600 py-7">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-xl md:text-3xl  font-bold text-white mb-4">
                Still Guessing Who to Hire?
              </h2>
              <p className=" text-base md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Join 260+ companies using AI to hire with 99% accuracy
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() =>
                    window.open("https://portal.selfsync.ai/sign-up", "_blank")
                  }
                  className="bg-white text-purple-600 px-6 py-2 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg flex items-center justify-center gap-2"
                >
                  <Users className="w-5 h-5" />
                  Start Free Trial
                </button>
                <button
                  onClick={() => navigate("/schedule-demo")}
                  className="border-2 border-white text-white px-6 py-2 rounded-lg font-bold hover:bg-white hover:text-purple-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  Schedule Demo
                </button>
              </div>

              <p className="text-sm text-white/80 mt-6">
                No credit card required • 2 free assessments • 15-minute setup
              </p>
            </motion.div>
          </div>
        </div>
      )}

      {/* Main Footer Content */}
      <div className="py-8 px-8 md:px-1 ">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div
                  className="flex items-center space-x-2 mb-6 cursor-pointer"
                  onClick={() => navigate("/")}
                >
                  <img src={logo} alt="SelfSync Logo" className="h-10" />
                </div>

                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  AI-powered talent assessment platform helping companies hire
                  with 99% accuracy. Make data-driven hiring decisions in
                  minutes, not weeks.
                </p>

                {/* Contact Info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">
                      4th Floor, Lalwani Triumph, Sakore Nagar Road,
                      <br />
                      Viman Nagar, Pune MH, 411014
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-purple-400" />
                    <a
                      href="mailto:support@selfsync.ai"
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      support@selfsync.ai
                    </a>
                  </div>
                  {/* <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-purple-400" />
                    <a 
                      href="tel:+919876543210" 
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      +91 98765 43210
                    </a>
                  </div> */}
                </div>

                {/* Social Links */}
                <div className="flex space-x-4">
                  {socialLinks.map((social) => (
                    <motion.a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gray-700 p-3 rounded-lg hover:bg-purple-600 transition-colors"
                      aria-label={social.name}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Quick Links */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <h3 className="font-bold mb-6 text-white text-lg">Product</h3>
                <ul className="space-y-3">
                  {quickLinks.map((link) => (
                    <li key={link.name}>
                      <button
                        onClick={() => navigate(link.path)}
                        className="text-gray-300 hover:text-white transition-colors text-left"
                      >
                        {link.name}
                      </button>
                    </li>
                  ))}
                  <li>
                    <a
                      href="https://portal.selfsync.ai/sign-up"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-white transition-colors flex items-center gap-2"
                    >
                      Go to Portal
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </li>
                </ul>
              </motion.div>
            </div>

            {/* Company */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h3 className="font-bold mb-6 text-white text-lg">Company</h3>
                <ul className="space-y-3">
                  {company.map((link) => (
                    <li key={link.name}>
                      <button
                        onClick={() => navigate(link.path)}
                        className="text-gray-300 hover:text-white transition-colors text-left"
                      >
                        {link.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Legal */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h3 className="font-bold mb-6 text-white text-lg">Legal</h3>
                <ul className="space-y-3">
                  {legal.map((link) => (
                    <li key={link.name}>
                      <NavLink
                        to={link.path}
                        className="text-gray-300 hover:text-white transition-colors"
                      >
                        {link.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>

                {/* Trust Badges */}
                <div className="mt-8">
                  <h4 className="font-semibold text-white mb-3">Security</h4>
                  <div className="space-y-2">
                    <div className="text-sm text-gray-300">
                      🔒 SOC 2 Compliant
                    </div>
                    <div className="text-sm text-gray-300">
                      🛡️ GDPR Compliant
                    </div>
                    <div className="text-sm text-gray-300">
                      🔐 SSL Encrypted
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Stats Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="border-t border-gray-700 pt-12 mt-12"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-white mb-2">99%</div>
                <div className="text-gray-300">Accuracy Rate</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-white mb-2">260+</div>
                <div className="text-gray-300">Companies Trust Us</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-white mb-2">60K+</div>
                <div className="text-gray-300">Assessments Completed</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-white mb-2">15 Min</div>
                <div className="text-gray-300">Setup Time</div>
              </div>
            </div>
          </motion.div>

          {/* Bottom Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="border-t border-gray-700 pt-8 mt-12 flex flex-col md:flex-row justify-between items-center"
          >
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © {moment().year()} SelfSync AI. All rights reserved.
            </div>

            <div className="flex items-center gap-6 text-sm">
              <span className="text-gray-400">Made with ❤️ in India</span>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
